import { useEffect, useState, useLayoutEffect, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import {
    UserSession,
    TeamsList,
    CurrentTeam
} from '../../utils'
import { Client, Account, Teams, Functions } from "appwrite";
import logo from "../../assets/tom-logo-icon-black.jpg"

import Popup from '../../components/Popup'
import { createUser } from '../../appwrite/methods'
import DashboardMenu from '../../components/DashboardMenu'
import UserAvatar from '../../components/Avatar';
import Skeleton from '@mui/material/Skeleton';
import { Query } from 'appwrite'
import ThemeOption from '../../components/ThemeOption'

import { PageContainer } from '@toolpad/core/PageContainer';
import Grid from '@mui/material/Grid2';
import { OutlinedCard } from '../../components/OutlinedCard';
import LogoLoader from '../../components/LogoLoader';
import { DataGrid } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Avatar from '@mui/material/Avatar';
import LinearProgress from '@mui/material/LinearProgress';


const client = new Client()
    .setEndpoint(process.env.REACT_APP_ENDPOINT)
    .setProject(process.env.REACT_APP_PROJECT_ID);

const account = new Account(client);
const teams = new Teams(client);
const functions = new Functions(client);

var mountedLoad = false;


const columns = [
    { field: 'id', headerName: 'ID', width: 50 },
    // {
    //     field: 'avatar',
    //     headerName: 'Avatar',
    //     width: 75,
    //     editable: false,
    // },
    {
        field: 'avatar',
        headerName: 'Avatar',
        display: 'flex',
        width: 75,
        renderCell: UserAvatar,
        valueGetter: (value, row) =>
          row.name == null || row.avatar == null
            ? null
            : { name: row.name, color: row.avatar },
        sortable: false,
        filterable: false,
        editable: false
    },
    {
        field: 'name',
        headerName: 'Username',
        width: 150,
        editable: true,
    },
    {
        field: 'email',
        headerName: 'Email',
        width: 250,
    },
    {
        field: 'team',
        headerName: 'Teams',
        width: 200,
        editable: true,
    },
    // TODO: permissions need to be handled on a per team basis; thus, this functionality should be moved into a different component
    // {
    //     field: 'permissions',
    //     headerName: 'Permissions',
    //     type: 'singleSelect',
    //     valueOptions: ['Admin'],
    //     width: 150,
    //     editable: true,
    // },
    // For info purposes
    // {
    //   field: 'fullName',
    //   headerName: 'Full name',
    //   description: 'This column has a value getter and is not sortable.',
    //   sortable: false,
    //   width: 160,
    //   valueGetter: (value, row) => `${row.firstName || ''} ${row.lastName || ''}`,
    // },
];


export default function TeamPermissions() {
    const navigate = useNavigate()
    const [userSession, setUserSession] = useAtom(UserSession);
    const [teamsList, setTeamsList] = useAtom(TeamsList);
    const [currentTeam, setCurrentTeam] = useAtom(CurrentTeam);
    
    const [rows, setRows] = useState([]);
    const [selectedTeams, setSelectedTeams] = useState([]);
    const [email, setEmail] = useState('');

    const [isLoading, setIsLoading] = useState(true);
    const [isVisible, setIsVisible] = useState(true);

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    // Get all users and list
    useEffect(() => {
        const getTeamUsers = async () => {
            try {
                const usersRaw = await functions.createExecution(
                    "users-get-users",
                    JSON.stringify({}),
                    false,
                    '/',
                    'GET'
                )
                const users = JSON.parse(usersRaw.responseBody).response.users
                const userRows = users.map((user, index) => ({
                    id: index + 1,
                    // TODO: add avatar
                    avatar: { name: user.name, avatar: user.avatar },
                    name: user.name,
                    email: user.email,
                    permissions: user.roles || '',
                    // team: user.teamName
                }));
                setRows(userRows);
            } catch (error) {
                console.error("Error fetching team users:", error);
            }
        };

        getTeamUsers();
    }, []);


    useEffect(() => {
        if (!mountedLoad) mountedLoad = true;
    }, []);

    // Set authenticated session on initial page load
    useEffect(() => {
        const checkSession = async () => {
            try {
                const user = await account.get()
                return user
            } catch (error) {
                navigate('/')
            }
        }
        setUserSession(checkSession())
    }, []);

    // Display loading screen while the page loads
    useLayoutEffect(() => {
        const timeoutId = setTimeout(() => {
            setIsLoading(false);
            setTimeout(() => {
                setIsVisible(false);
            }, 750);
        }, 0);
        
        return () => clearTimeout(timeoutId);
    }, []);

    return (<>
        {userSession && <DashboardMenu path="" user={userSession} needsOwner={true} page={userSession && (<div className="page-content">
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Typography variant='p' onClick={() => navigate('/team')} sx={{ cursor: 'pointer' }}>Team</Typography>
                <Typography variant='p' onClick={() => navigate('/team/members')} sx={{ cursor: 'pointer' }}>Members</Typography>
            </Breadcrumbs>
            <Typography variant="h4" gutterBottom>
                {teamsList[currentTeam]} Members
            </Typography>
            <Box sx={{ width: '100%', mt: 3 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 10,
                            },
                        },
                    }}
                    pageSizeOptions={[5]}
                    checkboxSelection
                    disableRowSelectionOnClick
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                        },
                    }}
                    slots={{
                        loadingOverlay: LinearProgress,
                    }}
                    sx={{
                        // '& .MuiDataGrid-cell:hover': {
                        //     color: 'primary.main',
                        // },
                    }}
                />
            </Box>
            <Popup
                openText="Add new user"
                openVariant="contained"
                header={"Invite new user"}
                sx={{ mt: 2 }}
                content={<>
                    <Typography variant="p" gutterBottom>
                        Send invitation to:
                    </Typography>
                    <br/>
                    <TextField
                        id="email-input"
                        label="Email"
                        variant="outlined"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{
                            '& .MuiInputBase-root': { height: '2.5em', overflow: 'hidden' },
                            fontSize: '0.5em',
                            width: '100%',
                            mb: 1.5
                        }}
                    />
                    <br/>
                    {/* <Typography variant="p" gutterBottom>
                        Add user to teams:
                    </Typography>
                    <br/>
                    <FormControl sx={{ width: '40vw', minWidth: '300px' }}>
                        <InputLabel id="demo-multiple-chip-label">Select teams</InputLabel>
                        <Select
                            labelId="demo-multiple-chip-label"
                            multiple
                            value={selectedTeams}
                            onChange={handleChange}
                            input={<OutlinedInput id="select-multiple-chip" label="Select teams" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip avatar={<Avatar src={logo} alt={`${value} team logo`} />} key={value} label={value} />
                                    ))}
                                </Box>
                            )}
                            MenuProps={MenuProps}
                        >
                            {allTeams.map((team) => (
                                <MenuItem
                                    key={team.$id}
                                    value={team.name}
                                >
                                    {team.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl> */}
                </>}
                buttonSuccess={"Create user"}
                // FIXME: this needs to send an email to the user and walk them through creating an account instead
                // TODO: this needs to create a magic link and send it in the email
                successAction={() => createUser("Test User", email, "test1234")}
                successDisabled={!email || !selectedTeams.length}
                buttonCancel={"Cancel"}
            />
        </div>)} />}

        {isVisible && !mountedLoad && (<div className={`logo-loader ${!isLoading ? 'hidden' : ''}`}>
            <LogoLoader />
        </div>)}
    </>)
}